import { UAParser } from 'ua-parser-js';
import { CONFIGURACION_SESION_MULTIPLE } from '@/config/configuracion';
import { generateUUID } from './uuid';

/**
 * Genera o recupera el ID único del dispositivo
 * Persiste en localStorage para mantener el mismo ID entre sesiones
 * 
 * @returns deviceId único y persistente
 */
export const getDeviceId = (): string => {
    const STORAGE_KEY = CONFIGURACION_SESION_MULTIPLE.CLAVE_STORAGE_DEVICE_ID;

    // Intentar obtener deviceId existente
    let deviceId = localStorage.getItem(STORAGE_KEY);

    if (!deviceId) {
        // Generar nuevo UUID
        deviceId = generateUUID();
        localStorage.setItem(STORAGE_KEY, deviceId);
    }

    return deviceId;
};

/**
 * Información del dispositivo actual
 */
export interface DeviceInfo {
    navegador: string;
    so: string;
    dispositivo: 'desktop' | 'mobile' | 'tablet';
    modelo: string;
}

/**
 * Obtiene información del dispositivo usando UAParser
 * 
 * @returns Información navegador, SO, tipo dispositivo, modelo
 */
export const getDeviceInfo = (): DeviceInfo => {
    const parser = new UAParser();
    const result = parser.getResult();

    // Determinar tipo de dispositivo
    let tipoDispositivo: 'desktop' | 'mobile' | 'tablet' = 'desktop';
    if (result.device.type === 'mobile') {
        tipoDispositivo = 'mobile';
    } else if (result.device.type === 'tablet') {
        tipoDispositivo = 'tablet';
    }

    // Construir nombre navegador
    const navegador = `${result.browser.name || 'Unknown'} ${result.browser.version || ''}`.trim();

    // Construir nombre SO
    const so = `${result.os.name || 'Unknown'} ${result.os.version || ''}`.trim();

    // Modelo dispositivo (desktop genérico si no hay info specific)
    const modelo = result.device.model || (tipoDispositivo === 'desktop' ? 'Desktop' : 'Unknown Device');

    return {
        navegador,
        so,
        dispositivo: tipoDispositivo,
        modelo,
    };
};

/**
 * Remueve el deviceId del localStorage (útil para testing)
 */
export const clearDeviceId = (): void => {
    const STORAGE_KEY = CONFIGURACION_SESION_MULTIPLE.CLAVE_STORAGE_DEVICE_ID;
    localStorage.removeItem(STORAGE_KEY);
};
